import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { gsap, ScrollTrigger, useGSAP, EASE } from "../../lib/gsap";
import { useSection } from "../../context/SectionContext";
import { useGround } from "../../context/GroundContext";
import { scrollToSection } from "../../lib/scrollTo";
import { chapters, personalInfo } from "../../data/portfolio";
import Magnetic from "../ui/Magnetic";
import Clock from "../ui/Clock";
import Icon from "../ui/Icon";

const LINKS = [
  { label: "GitHub", href: personalInfo.github },
  { label: "LinkedIn", href: personalInfo.linkedin },
  { label: "Résumé", href: personalInfo.cvUrl },
];

/**
 * Navigation as part of the instrument: a thin top bar, a vertical chapter
 * index on wide screens, a compact bar on touch, and one full-screen index.
 * The rail owns chapter tracking for the whole site (one set of ScrollTriggers)
 * and inverts itself when the active chapter is dark.
 */
const GROUND_LABEL = { auto: "Auto", light: "Light", dark: "Dark" };

const Rail = () => {
  const { activeId, active, setSection } = useSection();
  const { ground, cycle } = useGround();
  const [open, setOpen] = useState(false);
  const opener = useRef(null);
  const menuRef = useRef(null);
  const scope = useRef(null);

  useGSAP(() => {
    const triggers = chapters
      .map(({ id }) => {
        const el = document.getElementById(id);
        if (!el) return null;
        return ScrollTrigger.create({
          trigger: el,
          start: "top 45%",
          end: "bottom 45%",
          onToggle: (self) => self.isActive && setSection(id),
        });
      })
      .filter(Boolean);

    return () => triggers.forEach((t) => t.kill());
  }, { dependencies: [setSection] });

  const go = useCallback((id) => {
    setOpen(false);
    scrollToSection(id);
  }, []);

  // Escape closes, focus returns to the trigger, and the page stops scrolling
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        opener.current?.focus();
      }
      if (e.key === "Tab" && menuRef.current) {
        const focusables = menuRef.current.querySelectorAll("a[href], button:not([disabled])");
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.lenis?.stop();
    document.addEventListener("keydown", onKey);
    const t = window.setTimeout(() => {
      menuRef.current?.querySelector("[data-menu-first]")?.focus();
    }, 260);

    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      window.lenis?.start();
    };
  }, [open]);

  const dark =
    ground === "dark" ? true : ground === "light" ? false : active.tone === "dark";

  return (
    <div ref={scope}>
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header
        className={`veil hair-b fixed inset-x-0 top-0 z-rail flex h-14 items-center justify-between px-margin transition-colors duration-500 ease-out ${
          dark ? "chapter-dark" : ""
        }`}
      >
        {/* Left group — one flex child, so justify-between keeps three columns */}
        <div className="flex shrink-0 items-center gap-5">
          <button
            type="button"
            onClick={() => go("home")}
            className="flex items-center gap-2.5"
            data-cursor-label="Top"
          >
            <span className="reg-mark" aria-hidden="true" />
            <span className="t-label text-fg">{personalInfo.fullName}</span>
          </button>

          <Clock className="t-label hidden xl:inline" />
        </div>

        {/* Chapter readout — centred on the bar, out of the flow */}
        <span className="t-label pointer-events-none absolute left-1/2 hidden -translate-x-1/2 md:block">
          <span className="text-flare">{active.index}</span> — {active.title}
        </span>

        <div className="flex shrink-0 items-center gap-5 md:gap-7">
          <button
            type="button"
            onClick={cycle}
            className="t-label flex min-h-[44px] items-center gap-2 px-1 text-fg"
            aria-label={`Ground: ${GROUND_LABEL[ground]}. Activate to change.`}
            data-cursor-label="Ground"
          >
            <span className="hidden lg:inline">Ground</span>
            <span className="flex items-end gap-[3px]" aria-hidden="true">
              {["auto", "light", "dark"].map((step, i) => (
                <span
                  key={step}
                  className="block w-[3px] transition-colors duration-200"
                  style={{
                    height: 5 + i * 2,
                    background: ground === step ? "var(--flare)" : "var(--line-strong)",
                  }}
                />
              ))}
            </span>
            <span className="text-fg">{GROUND_LABEL[ground]}</span>
          </button>

          <Magnetic padding={16}>
            <button
              ref={opener}
              type="button"
              onClick={() => setOpen(true)}
              className="flex items-center gap-3 py-2"
              aria-expanded={open}
              aria-haspopup="dialog"
              data-cursor-label="Menu"
            >
              <span className="t-label-strong">Menu</span>
              <Icon name="menu" size={18} />
            </button>
          </Magnetic>
        </div>
      </header>

      {/* ── Vertical chapter index (wide screens) ───────────────── */}
      <nav
        aria-label="Chapters"
        className={`fixed right-0 top-1/2 z-rail hidden -translate-y-1/2 flex-col items-end gap-3 pr-margin xl:flex ${
          dark ? "chapter-dark" : ""
        }`}
      >
        {chapters.map((chapter) => {
          const current = chapter.id === activeId;
          return (
            <button
              key={chapter.id}
              type="button"
              onClick={() => go(chapter.id)}
              aria-current={current ? "true" : undefined}
              className="group flex items-center gap-3 py-1.5"
              data-cursor-label={chapter.label}
            >
              <span
                className={`t-label transition-all duration-300 ease-out ${
                  current
                    ? "text-flare opacity-100"
                    : "text-dim opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
                }`}
              >
                {chapter.label}
              </span>
              <span
                className="block h-px transition-all duration-500 ease-out"
                style={{
                  width: current ? 44 : 18,
                  background: current ? "var(--flare)" : "var(--line-strong)",
                }}
              />
              <span className={`t-num text-[0.625rem] ${current ? "text-fg" : "text-dim"}`}>
                {chapter.index}
              </span>
            </button>
          );
        })}
      </nav>

      {/* ── Touch bar ───────────────────────────────────────────── */}
      <div
        className={`veil hair-t fixed inset-x-0 bottom-0 z-rail flex items-center justify-between px-margin py-3 md:hidden ${
          dark ? "chapter-dark" : ""
        }`}
      >
        <span className="t-label">
          <span className="text-flare">{active.index}</span> {active.title}
        </span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="t-label-strong flex min-h-[44px] items-center gap-2 px-2"
          aria-haspopup="dialog"
        >
          Menu
          <Icon name="menu" />
        </button>
      </div>

      {/* ── Full-screen index ───────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="index"
            ref={menuRef}
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            className="chapter-dark fixed inset-0 z-menu flex flex-col bg-ink px-margin py-6 text-bone"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: [0.77, 0, 0.175, 1] }}
          >
            <div className="flex items-center justify-between">
              <span className="t-label text-ash">Menu</span>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  opener.current?.focus();
                }}
                className="t-label-strong flex min-h-[44px] items-center gap-2 px-2 text-bone"
              >
                Close
                <Icon name="close" />
              </button>
            </div>

            <nav aria-label="Chapters" className="flex flex-1 flex-col justify-center">
              {chapters.map((chapter, i) => (
                <div key={chapter.id} className="hair-b overflow-hidden">
                  <motion.button
                    type="button"
                    onClick={() => go(chapter.id)}
                    data-menu-first={i === 0 ? "true" : undefined}
                    className="flex w-full items-baseline justify-between gap-6 py-[clamp(0.5rem,1.6vh,1.25rem)] text-left"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.12 + i * 0.05 }}
                  >
                    <span
                      className="text-bone uppercase"
                      style={{
                        fontSize: "clamp(1.75rem, 7.2vh, 5.5rem)",
                        lineHeight: 0.9,
                        letterSpacing: "-0.035em",
                        fontVariationSettings: "'wdth' 94, 'wght' 620",
                      }}
                    >
                      {chapter.label}
                    </span>
                    <span className="t-num text-xs text-ash">{chapter.index}</span>
                  </motion.button>
                </div>
              ))}
            </nav>

            <div className="flex flex-wrap items-center justify-between gap-4">
              <a href={`mailto:${personalInfo.email}`} className="t-label link-wipe text-bone">
                {personalInfo.email}
              </a>
              <div className="flex flex-wrap gap-5">
                {LINKS.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="t-label link-wipe flex items-center gap-1.5 text-bone"
                  >
                    {link.label}
                    <Icon name="arrowOut" />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export { EASE };
export default Rail;
